import type { Scale, Text, Field } from '@eformulieren/model'
import { type ReactNode, useEffect, useId, useRef, useState } from 'react'
import { BusyButton } from '../../shell/BusyButton.js'
import type { LaagProps } from './kinds.js'

/**
 * Het criterium: een oordeel op een instelbare schaal, met een verplichte onderbouwing
 * (R-20, R-147, R-148, R-150).
 *
 * **Eén control en geen samenstel van bestaande velden**, en dat is wat `R-20` letterlijk eist.
 * Een schaal plus een tekstveld naast elkaar zou hetzelfde lijken en drie dingen missen: de
 * onderbouwing zou weg te laten zijn, het feit of het criterium is afgedaan zou uit de
 * schaalwaarde moeten worden geraden, en het geheel zou per aanslag naar de opslag gaan terwijl
 * een oordeel er in één keer is.
 *
 * **Twee schaalvormen, uit het criterium en niet uit de control** (R-148). Een tweekeuze zoals
 * voldoet of voldoet niet, of een cijferbereik zoals 1 tot 9. Bij een cijferbereik mag er per
 * waarde een label staan, want het voorbeeldbeoordelingsformulier uit extract 214 loopt van 1 tot
 * 9 met Excellent, Zeer goed, Goed, Matig en Slecht erboven. Er is geen richting bij: in dat
 * formulier is 1 de beste score, en die richting zit al in de labels.
 *
 * **Afdoen is een eigen feit en geen afgeleide.** Bij een tweekeuze volgt het uit de gekozen
 * waarde en dat is de hele betekenis van die twee opties. Bij een cijferbereik niet: uit een 6
 * van 9 volgt niet of dat voldoet, want waar de grens ligt is een instelling per ronde en die
 * hoort niet in het formulier (`R-151`). Daar staat de vraag dus apart, met beide antwoorden even
 * zichtbaar en geen voorgeselecteerde kant.
 *
 * **Zonder aanraking is er geen oordeel**, dezelfde regel als bij `Schaal.tsx` en om dezelfde
 * reden (ontwerp 6.2): een knoppenrij die al ergens op lijkt te staan laat de lezer denken dat er
 * iets is gekozen. En nog een keer op dezelfde waarde wist hem, zodat een misklik terug te nemen
 * is zolang het oordeel nog niet is gegeven.
 *
 * **Eenmalig, en daarna alleen-lezen.** Precies één oordeel per criterium per instantie; een
 * tweede oordeel is een nieuwe instantie in de keten (U21, R-192). De control laat na het vellen
 * zien wat er staat, en biedt geen weg terug die de opslag toch zou weigeren.
 *
 * **De machine-uitkomst staat ernaast en beslist niet** (R-180, ontwerp 7.4). Wat een aangewezen
 * controle heeft vastgesteld is hulp bij het oordeel; het oordeel is altijd van een mens
 * (`R-149` is als instelling vervallen). Daarom staat ze boven het oordeelveld en niet erin, en
 * vult ze niets voor.
 */
export function Criterion(props: LaagProps): ReactNode {
  const { veld: field, taal: language, koppeling: link, uitkomst: outcome, bewerkbaar: editable } = props
  asCriterion(field)

  const given = outcome?.oordeel
  const issuedRef = useRef<HTMLDListElement>(null)
  // Alleen bij de overgang van onbeantwoord naar gegeven, niet bij een criterium dat al
  // beantwoord was toen dit component ontstond: dat zou de focus wegkapen van iets waar de
  // toetser nog helemaal niets mee deed.
  const previousGiven = useRef(given)
  useEffect(() => {
    if (given !== undefined && previousGiven.current === undefined) {
      issuedRef.current?.focus()
    }
    previousGiven.current = given
  }, [given])

  return (
    <div className="ef-criterium" id={link.id} aria-describedby={link['aria-describedby']}>
      {outcome?.machine === undefined ? null : (
        <p className="ef-criterium__machine">
          <span className="ef-criterium__machinekop">{MACHINE[language]}</span>{' '}
          <span className="ef-criterium__machinestand" data-voldoet={outcome.machine.voldoet}>
            {outcome.machine.voldoet ? PASSED[language] : NOT_PASSED[language]}
          </span>
          {outcome.machine.detail === '' ? null : (
            <span className="ef-criterium__machinedetail">{outcome.machine.detail}</span>
          )}
        </p>
      )}
      {given === undefined ? (
        <Fillable {...props} />
      ) : (
        // `tabIndex={-1}`: programmatisch te focussen, niet in de gewone tabvolgorde. De knop
        // die net de klik of Enter ontving verdwijnt bij het vellen uit de DOM, en zonder
        // gerichte focus valt de browser dan terug op body (WCAG 2.4.3, M8.12). Een eigen
        // `aria-label` en niet alleen de focusverplaatsing: anders krijgt het element wel focus
        // maar zegt niets waarom, en dat was precies het gebrek in het gekalibreerde precedent
        // (M5.9e, een alertdialog die focus kreeg maar nooit werd voorgelezen).
        <dl
          className="ef-criterium__geveld"
          ref={issuedRef}
          tabIndex={-1}
          aria-label={VERDICT_RECORDED[language]}
        >
          <dt>{VERDICT[language]}</dt>
          <dd>{labelForValue(props, given.oordeel)}</dd>
          <dt>{SETTLED[language]}</dt>
          <dd>{given.voldoet ? YES[language] : NO[language]}</dd>
          <dt>{JUSTIFICATION[language]}</dt>
          <dd>{given.onderbouwing}</dd>
        </dl>
      )}
      {given === undefined && !editable ? (
        <p className="ef-criterium__leeg">{NO_VERDICT_YET[language]}</p>
      ) : null}
    </div>
  )
}

/** De invulkant: de schaal, het afdoen en de onderbouwing, met het oordeel als geheel. */
function Fillable({ veld: field, taal: language, koppeling: link, onVerdict, bewerkbaar: editable }: LaagProps): ReactNode {
  const criterion = asCriterion(field)
  const [chosen, setChosen] = useState<string | number | undefined>(undefined)
  const [passes, setPasses] = useState<boolean | undefined>(undefined)
  const [justification, setJustification] = useState('')
  // Wat er nog ontbrak toen de toetser op "Oordeel vastleggen" drukte; daarvoor staat er niets.
  const [tried, setTried] = useState(false)
  const hintId = useId()
  const missingId = useId()
  const scaleRef = useRef<HTMLDivElement>(null)
  const settleRef = useRef<HTMLFieldSetElement>(null)
  const textRef = useRef<HTMLTextAreaElement>(null)

  const scale = criterion.schaal
  // Bij een tweekeuze zegt de gekozen waarde zelf of het criterium is afgedaan; dat is de
  // betekenis van die twee opties. Bij een cijferbereik is het een eigen vraag (R-148).
  const options = scale.soort === 'tweekeuze' ? twoChoiceOptions(scale.opties, language) : []
  const settled =
    scale.soort === 'tweekeuze'
      ? options.find((option) => option.waarde === chosen)?.voldoet
      : passes
  const complete = chosen !== undefined && settled !== undefined && justification.trim() !== ''
  const missing = missingParts(chosen !== undefined, settled !== undefined, justification.trim() !== '', scale.soort, language)
  const header = criterion.onderbouwing?.label[language] ?? JUSTIFICATION[language]

  // Roving tabindex (WAI-ARIA APG, radiogroup): precies één knop in de gewone tabvolgorde, de
  // rest op -1. Zonder dit patroon belooft de rol pijltjesnavigatie die er niet is, en moet een
  // toetsenbordgebruiker bij negen punten negen keer Tabben (M8.12).
  const tabIndexFor = (value: string | number, index: number): 0 | -1 =>
    chosen === value || (chosen === undefined && index === 0) ? 0 : -1

  return (
    <>
      <div
        ref={scaleRef}
        className="ef-criterium__schaal"
        data-soort={scale.soort}
        role="radiogroup"
        aria-labelledby={link['aria-labelledby'] ?? link.id}
        aria-required={link['aria-required']}
        onKeyDown={(event) => {
          const NEXT = ['ArrowRight', 'ArrowDown']
          const PREVIOUS = ['ArrowLeft', 'ArrowUp']
          if (!NEXT.includes(event.key) && !PREVIOUS.includes(event.key)) return
          const buttons = [
            ...event.currentTarget.querySelectorAll<HTMLButtonElement>(
              '[role="radio"]:not(:disabled)',
            ),
          ]
          if (buttons.length === 0) return
          const current = buttons.indexOf(document.activeElement as HTMLButtonElement)
          const step = NEXT.includes(event.key) ? 1 : -1
          const next = buttons[(Math.max(current, 0) + step + buttons.length) % buttons.length]
          event.preventDefault()
          next?.focus()
          next?.click()
        }}
      >
        {scale.soort === 'tweekeuze'
          ? options.map((option, index) => (
              <button
                type="button"
                key={option.waarde}
                className="ef-criterium__punt"
                role="radio"
                aria-checked={chosen === option.waarde}
                data-voldoet={option.voldoet}
                tabIndex={tabIndexFor(option.waarde, index)}
                disabled={!editable}
                onClick={() => setChosen(chosen === option.waarde ? undefined : option.waarde)}
              >
                {option.label}
              </button>
            ))
          : numberRangePoints(scale, language).map((point, index) => (
              <button
                type="button"
                key={point.waarde}
                className="ef-criterium__punt"
                role="radio"
                aria-checked={chosen === point.waarde}
                tabIndex={tabIndexFor(point.waarde, index)}
                {...(point.label === undefined ? {} : { 'aria-label': `${point.waarde}: ${point.label}` })}
                disabled={!editable}
                onClick={() => setChosen(chosen === point.waarde ? undefined : point.waarde)}
              >
                <span className="ef-criterium__cijfer">{point.waarde}</span>
                {point.label === undefined ? null : (
                  <span className="ef-criterium__band">{point.label}</span>
                )}
              </button>
            ))}
      </div>

      {scale.soort === 'tweekeuze' ? null : (
        <fieldset className="ef-criterium__afdoen" disabled={!editable} ref={settleRef}>
          <legend>{SETTLED_QUESTION[language]}</legend>
          {[true, false].map((side) => (
            <label key={String(side)} className="ef-criterium__afdoenkeuze">
              <input
                type="radio"
                name={`${link.id}-afgedaan`}
                checked={passes === side}
                onChange={() => setPasses(side)}
              />
              {side ? YES[language] : NO[language]}
            </label>
          ))}
        </fieldset>
      )}

      {/*
        Een gewoon invoervak van drie, vier regels in de letter van het formulier (ontwerp van het
        toetsscherm), en geen kaal vak van één regel in de letter van de browser. Geen sterretje
        achter het label: een criterium is geen invoerveld (`NOT_INPUT`), en de uitlegzin over het
        sterretje staat alleen boven een formulier met verplichte invoervelden (besluit van 1
        november 2026). Dat de toelichting bij elk oordeel hoort zegt de hulptekst (R-09, R-147).
      */}
      <label className="ef-criterium__onderbouwing">
        <span className="ef-criterium__onderbouwingkop">{header}</span>
        <span className="ef-criterium__hint" id={hintId}>
          {JUSTIFICATION_HINT[language]}
        </span>
        <textarea
          ref={textRef}
          className="ef-invoer ef-invoer--meerregelig ef-criterium__tekst"
          rows={4}
          value={justification}
          disabled={!editable}
          aria-describedby={hintId}
          onChange={(event) => setJustification(event.target.value)}
        />
      </label>

      {/*
        Aan, ook als er nog iets ontbreekt (besluit van 30 oktober 2026, audit van het toetsscherm).
        Uitgezet in bleek teal leek de knop kapot, en de lange uitleg ernaast stond er altijd. Nu
        zegt een klik wat er nog ontbreekt, in één korte zin die aan de knop hangt, en gaat de focus
        naar het eerste wat ontbreekt. Er gaat dan niets weg: een oordeel is er in één keer (R-147).
      */}
      <div className="ef-criterium__vellenrij">
        {tried && !complete ? (
          <p className="ef-criterium__watmist" id={missingId}>
            {missing}
          </p>
        ) : null}
        <BusyButton
          className="ef-criterium__vellen"
          disabled={!editable}
          busyText={RECORDING[language]}
          {...(tried && !complete ? { 'aria-describedby': missingId } : {})}
          onAction={() => {
            if (chosen === undefined || settled === undefined || justification.trim() === '') {
              setTried(true)
              const first =
                chosen === undefined
                  ? (scaleRef.current?.querySelector<HTMLElement>('[role="radio"][tabindex="0"]') ?? undefined)
                  : settled === undefined
                    ? (settleRef.current?.querySelector<HTMLElement>('input') ?? undefined)
                    : (textRef.current ?? undefined)
              first?.focus()
              return undefined
            }
            // Een belofte als de pagina er een geeft: dan wacht de knop op de pod (`BusyButton`).
            return onVerdict({ oordeel: chosen, voldoet: settled, onderbouwing: justification.trim() })
          }}
        >
          {RECORD_VERDICT[language]}
        </BusyButton>
      </div>
    </>
  )
}

/**
 * Het veld als criteriumveld, of een fout.
 *
 * De registry kiest de control op `veld.control`, dus in de praktijk klopt het altijd. Deze
 * versmalling staat er omdat de unie in het model dat niet weet, en gooien is hier beter dan
 * een half getekend veld: dat is dezelfde regel als bij `requireSingle` en de andere versmallers.
 */
function asCriterion(field: Field): Extract<Field, { control: 'criterium' }> {
  if (field.control !== 'criterium') {
    throw new Error(`Criterium verwacht een criteriumveld, kreeg ${field.control}`)
  }
  return field
}

/**
 * De twee opties van een tweekeuze, met de standaard uit het model als er geen staan.
 *
 * Geëxporteerd voor het canvas van de configurator: dat toont een criterium zoals de toetser het
 * krijgt, en dan met dezelfde woorden als hier en niet met een eigen kopie ervan.
 */
export function twoChoiceOptions(
  options: Extract<Scale, { soort: 'tweekeuze' }>['opties'],
  language: 'nl' | 'en',
): readonly { waarde: string; label: string; voldoet: boolean }[] {
  if (options === undefined) {
    return [
      { waarde: 'voldoet', label: PASSED[language], voldoet: true },
      { waarde: 'voldoet-niet', label: NOT_PASSED[language], voldoet: false },
    ]
  }
  // De eerste optie doet af en de tweede niet; dat is de orde van het model (`Scale`, een
  // tuple van twee) en niet een gok op de tekst. Zou het uit de woorden worden gelezen, dan
  // zou een eigen paar als "akkoord" en "bezwaar" verkeerd uitpakken.
  return options.map((option, place) => ({
    waarde: option.waarde,
    label: option.label[language],
    voldoet: place === 0,
  }))
}

/**
 * De punten van een cijferbereik, met het label van de waarde waar er een staat.
 *
 * Elk punt tussen minimum en maximum krijgt een knop, ook de punten zonder label. In extract 214
 * loopt de schaal van 1 tot 9 met een woord bij 1, 3, 5, 7 en 9; wie alleen de gelabelde punten
 * zou tonen maakt van een schaal van negen er een van vijf, en dan is een 6 niet te geven.
 */
export function numberRangePoints(
  scale: Extract<Scale, { soort: 'cijferbereik' }>,
  language: 'nl' | 'en',
): readonly { waarde: number; label?: string }[] {
  const count = scale.maximum - scale.minimum + 1
  return Array.from({ length: count }, (_, place) => {
    const value = scale.minimum + place
    const band = scale.waarden?.find((point) => point.waarde === value)
    return band === undefined ? { waarde: value } : { waarde: value, label: band.label[language] }
  })
}

const MACHINE: Text = { nl: 'De controle stelde vast:', en: 'The check found:' }
const PASSED: Text = { nl: 'Voldoet', en: 'Meets' }
const NOT_PASSED: Text = { nl: 'Voldoet niet', en: 'Does not meet' }
const VERDICT: Text = { nl: 'Oordeel', en: 'Judgement' }
// "Klaar" en niet "Afgedaan" op het scherm (verzoek van de gebruiker, 1 november 2026): afgedaan is
// geen gewoon Nederlands voor een beoordelaar. Alleen de schermtekst veranderde; het contract
// houdt zijn element `voldoet` en zijn omschrijving (R-148).
const SETTLED: Text = { nl: 'Klaar', en: 'Done' }
const SETTLED_QUESTION: Text = {
  nl: 'Is dit criterium hiermee klaar?',
  en: 'Is this criterion done?',
}
const JUSTIFICATION: Text = { nl: 'Onderbouwing', en: 'Substantiation' }
const YES: Text = { nl: 'Ja', en: 'Yes' }
const NO: Text = { nl: 'Nee', en: 'No' }
const RECORD_VERDICT: Text = { nl: 'Oordeel vastleggen', en: 'Record judgement' }
const RECORDING: Text = { nl: 'Bezig met vastleggen…', en: 'Recording…' }
const JUSTIFICATION_HINT: Text = { nl: 'Verplicht bij elk oordeel.', en: 'Required with every judgement.' }

/**
 * Wat er nog ontbreekt voor een oordeel, als één korte zin. Zonder aanspreekvorm, zodat hij bij
 * de toetser en bij de beoordelaar gelijk leest.
 */
function missingParts(
  hasValue: boolean,
  hasSettled: boolean,
  hasText: boolean,
  kind: Scale['soort'],
  language: 'nl' | 'en',
): string {
  const parts: string[] = []
  if (!hasValue) parts.push(language === 'nl' ? 'een oordeel' : 'a judgement')
  // Bij een tweekeuze volgt het afdoen uit de keuze; dan hoort het niet in de zin.
  if (!hasSettled && kind === 'cijferbereik') parts.push(language === 'nl' ? 'of het criterium klaar is' : 'whether the criterion is done')
  if (!hasText) parts.push(language === 'nl' ? 'een toelichting' : 'an explanation')
  const list = parts.length < 2 ? (parts[0] ?? '') : `${parts.slice(0, -1).join(', ')} ${language === 'nl' ? 'en' : 'and'} ${parts.at(-1) ?? ''}`
  return language === 'nl' ? `Nog nodig: ${list}.` : `Still needed: ${list}.`
}
const VERDICT_RECORDED: Text = { nl: 'Oordeel vastgelegd', en: 'Judgement recorded' }
const NO_VERDICT_YET: Text = {
  nl: 'Hier komt het oordeel te staan zodra het is gegeven.',
  en: 'The judgement will appear here once it has been given.',
}

/** Het label bij een gegeven oordeel, zodat er niet alleen een cijfer staat. */
function labelForValue(props: LaagProps, value: string | number): string {
  return verdictLabel(props.veld, value, props.taal) ?? String(value)
}

/**
 * Het leesbare label van een oordeelwaarde, of niets als de waarde niet op de schaal staat.
 *
 * Geëxporteerd voor de vorige uitslag bij een hertoets (`PreviousResult`): daar stond de ruwe
 * waarde van de optie, "voldoet-niet", in beeld (audit van 30 oktober 2026). Een waarde die niet
 * op de schaal staat geeft niets terug, zodat de aanroeper geen sleutel als tekst toont.
 */
export function verdictLabel(field: Field, value: string | number, language: 'nl' | 'en'): string | undefined {
  if (field.control !== 'criterium') return undefined
  const scale = field.schaal
  if (scale.soort === 'tweekeuze') {
    return twoChoiceOptions(scale.opties, language).find((chance) => chance.waarde === value)?.label
  }
  if (typeof value !== 'number') return undefined
  const band = scale.waarden?.find((point) => point.waarde === value)
  return band === undefined ? String(value) : `${value}: ${band.label[language]}`
}
